import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { CircularProgress, Box } from '@mui/material';

/**
 * Check if a path is allowed based on allowed_routes
 * Handles parent/child route matching (e.g., /research allows /research/portfolio)
 */
function isRouteAllowed(currentPath, allowedRoutes) {
  if (!allowedRoutes || allowedRoutes.length === 0) {
    return true; // No restrictions
  }

  const allowedSet = new Set(allowedRoutes);

  // Direct match
  if (allowedSet.has(currentPath)) {
    return true;
  }

  // Check if current path is a child of an allowed route
  // e.g., if "/research" is allowed, "/research/portfolio" should also be allowed
  for (const route of allowedRoutes) {
    if (currentPath.startsWith(route + '/')) {
      return true;
    }
    // Also check if a parent of the current path is allowed
    // e.g., if "/research/portfolio" is allowed, we might want to allow /research too
    if (route.startsWith(currentPath + '/')) {
      return true;
    }
  }

  return false;
}

/**
 * ProtectedRoute component that requires authentication.
 * Redirects to login if user is not authenticated.
 * Also enforces allowed_routes restrictions from user's roles.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authenticated
 * @param {string[]} props.roles - Optional array of required role slugs
 */
export default function ProtectedRoute({ children, roles = [] }) {
  const { user, loading } = useUser();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900"
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements (if any)
  if (roles.length > 0) {
    const normalize = (value) => (value == null ? '' : String(value)).toLowerCase().trim();
    const fromRoleSlugs = Array.isArray(user?.role_slugs) ? user.role_slugs : [];
    const fromRoles = (user?.roles || []).map((r) => {
      if (r == null) return null;
      if (typeof r === 'string' || typeof r === 'number') return r;
      return r.slug || r.name || r.id || null;
    });
    const fromDirectRole = user?.role ? [user.role] : [];
    const userRoles = Array.from(
      new Set([...fromRoleSlugs, ...fromRoles, ...fromDirectRole].map(normalize).filter(Boolean)),
    );
    const hasRequiredRole = roles.some(role => userRoles.includes(normalize(role)));

    if (!hasRequiredRole) {
      return (
        <Box
          p={4}
          textAlign="center"
          className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900"
        >
          <div className="content-shell max-w-md">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              You don't have permission to view this page.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Required roles: {roles.join(', ')}
            </p>
          </div>
        </Box>
      );
    }
  }

  // Check allowed_routes restrictions
  const allowedRoutes = user?.allowed_routes;
  if (allowedRoutes && allowedRoutes.length > 0) {
    const currentPath = location.pathname;

    // Always allow certain essential routes
    const alwaysAllowedPaths = ['/login', '/logout', '/account-settings', '/help'];
    const isEssentialRoute = alwaysAllowedPaths.some(path =>
      currentPath === path || currentPath.startsWith(path + '/')
    );

    if (!isEssentialRoute && !isRouteAllowed(currentPath, allowedRoutes)) {
      // Auto-redirect to first allowed route instead of showing error page
      const defaultRoute = allowedRoutes[0] || '/';
      return <Navigate to={defaultRoute} replace />;
    }
  }

  // Authorized - render children
  return children;
}
